<template>
  <div class="lead-detail-content">
    <LoadingSpinner v-if="loading" />

    <div v-else-if="!lead" class="lead-detail-content__error">
      <BaseIcon name="alert-circle" size="48" />
      <h3>{{ $t('lead_details.lead_not_found') }}</h3>
      <p>{{ $t('lead_details.lead_not_found_description') }}</p>
    </div>

    <div v-else class="lead-detail-content__wrapper">
      <!-- Lead Header -->
      <div class="lead-header">
        <div class="lead-header__info">
          <div class="lead-header__avatar">
            <AvatarElement
              :title="lead.name ? lead.name.substring(0, 2).toUpperCase() : 'LD'"
              :color="'var(--brand-500)'"
              :textColor="'white'"
            />
          </div>
          <div class="lead-header__details">
            <div class="lead-header__name-row">
              <h1 class="lead-header__name">{{ headerName || $t('lead_details.unnamed_lead') }}</h1>
              <span v-if="isCompany" class="lead-header__type-badge">
                <BaseIcon name="building" size="12" />
                {{ $t('lead_details.customer_type.company') }}
              </span>
            </div>
            <p class="lead-header__email">{{ lead.email }}</p>
            <div class="lead-header__meta">
              <span
                class="lead-header__status"
                :class="`lead-header__status--${lead.status || 'new'}`"
              >
                <div class="status-dot" :class="`status-dot--${lead.status || 'new'}`"></div>
                {{ getStatusLabel(lead.status) }}
              </span>
              <span class="lead-header__date">
                {{ $t('lead_details.created_at') }}: {{ formatDate(lead.created_at) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="lead-tabs">
        <Tabs v-model="activeTab">
          <Tab name="overview" :title="$t('lead_details.overview')">
            <div class="lead-details" v-if="!loading">
              <div class="lead-details__content">
                <div class="row">
                  <div class="col-lg-6">
                    <div class="lead-details__main">
                      <div class="lead-details__fields">
                        <div class="lead-details__section-header">
                          <h3 class="mb-0">{{ $t('lead_details.contact_details') }}</h3>
                          <Btn
                            variant="tertiary"
                            size="sm"
                            icon="edit"
                            @click="openEditModal"
                          >
                            {{ $t('lead_details.edit_details') }}
                          </Btn>
                        </div>
                        <div class="field-item" v-if="localLead">
                          <label>
                            <BaseIcon name="user" size="20" />
                            {{ $t('lead_details.name') }}
                          </label>
                          <InplaceEdit
                            :modelValue="localLead.name"
                            @update="updateField('name', $event)"
                          >
                            <template #editor="{ value, update, save, cancel }">
                              <div class="field-item__editor">
                                <TextInput
                                  type="text"
                                  :modelValue="value"
                                  @update:modelValue="update"
                                  @blur="save"
                                  @keydown.enter="save"
                                  @keydown.esc="cancel"
                                  ref="input"
                                />
                                <BaseIcon name="check-circle" size="18" @click="save" />
                                <BaseIcon name="xmark" size="18" @click="cancel" />
                              </div>
                            </template>
                          </InplaceEdit>
                        </div>
                          <div class="field-item" v-if="localLead">
                            <label>
                            <BaseIcon name="mail" size="20" />
                            {{ $t('lead_details.email') }}
                          </label>
                          <InplaceEdit
                            :modelValue="localLead.email"
                            @update="updateField('email', $event)"
                          >
                            <template #editor="{ value, update, save, cancel }">
                              <div class="field-item__editor">
                                <TextInput
                                  type="email"
                                  :modelValue="value"
                                  @update:modelValue="update"
                                  @blur="save"
                                  @keydown.enter="save"
                                  @keydown.esc="cancel"
                                  ref="input"
                                />
                                <BaseIcon name="check-circle" size="18" @click="save" />
                                <BaseIcon name="xmark" size="18" @click="cancel" />
                              </div>
                            </template>
                          </InplaceEdit>
                        </div>
                        <div class="field-item" v-if="localLead">
                          <label>
                            <BaseIcon name="phone" size="20" />
                            {{ $t('lead_details.phone') }}
                          </label>
                          <InplaceEdit
                            :modelValue="localLead.phone"
                            @update="updateField('phone', $event)"
                          >
                            <template #display>
                              <div class="field-item__phone" v-if="localLead.phone">
                                <span>{{ formatPhone(localLead.phone) }}</span>
                                <a
                                  :href="toTelHref(localLead.phone)"
                                  class="field-item__phone-link"
                                  @click.stop
                                >
                                  <BaseIcon name="phone" size="14" />
                                </a>
                              </div>
                              <span v-else class="inplace-edit__display--empty">
                                {{ $t('lead_details.no_phone_provided') || '—' }}
                              </span>
                            </template>
                            <template #editor="{ value, update, save, cancel }">
                              <div class="field-item__editor">
                                <TextInput
                                  type="tel"
                                  :modelValue="value"
                                  @update:modelValue="update"
                                  @blur="save"
                                  @keydown.enter="save"
                                  @keydown.esc="cancel"
                                  ref="input"
                                />
                                <BaseIcon name="check-circle" size="18" @click="save" />
                                <BaseIcon name="xmark" size="18" @click="cancel" />
                              </div>
                            </template>
                          </InplaceEdit>
                        </div>
                        <div class="field-item" v-if="localLead">
                          <label>
                            <BaseIcon name="map-pin" size="20" />
                            {{ $t('lead_details.address') }}
                          </label>
                          <InplaceEdit
                            :modelValue="localLead.address || {}"
                            @update="updateField('address', $event)"
                          >
                            <template #display>
                              <div class="field-item__value">
                                {{
                                  formatAddress(localLead.address) ||
                                  $t('lead_details.no_address_provided')
                                }}
                              </div>
                            </template>
                            <template #editor="{ value, update, save, cancel }">
                              <div class="field-item__editor field-item__editor--address">
                                <AddressInput
                                  :id="`lead-address-${leadId}`"
                                  :modelValue="value || {}"
                                  @update:modelValue="update"
                                />
                                <div class="field-item__editor-actions">
                                  <BaseIcon name="check-circle" size="18" @click="save" />
                                  <BaseIcon name="xmark" size="18" @click="cancel" />
                                </div>
                              </div>
                            </template>
                          </InplaceEdit>
                        </div>
                        <div class="field-item" v-if="localLead">
                          <label>
                            <BaseIcon name="info" size="20" />
                            {{ $t('lead_details.description') }}
                          </label>
                          <InplaceEdit
                            :modelValue="localLead.notes || ''"
                            @update="updateField('notes', $event)"
                            multiline
                          >
                            <template #display>
                              <div
                                v-if="localLead.notes"
                                class="field-item__html-content"
                                v-html="localLead.notes"
                              ></div>
                              <span v-else class="inplace-edit__display--empty">
                                {{ $t('lead_details.no_description_provided') }}
                              </span>
                            </template>
                            <template #editor="{ value, update, save, cancel }">
                              <div class="field-item__editor">
                                <WysiwygEditor :modelValue="value" @update:modelValue="update" />
                                <div class="field-item__editor-actions">
                                  <BaseIcon name="check-circle" size="18" @click="save" />
                                  <BaseIcon name="xmark" size="18" @click="cancel" />
                                </div>
                              </div>
                            </template>
                          </InplaceEdit>
                        </div>
                      </div>

                      <div
                        v-if="isCompany && localLead"
                        class="lead-details__fields lead-details__fields--readonly"
                      >
                        <h3 class="mb-6">{{ $t('lead_details.company_details') }}</h3>
                        <div class="field-item field-item--readonly" v-if="localLead.company_name">
                          <label>
                            <BaseIcon name="building" size="20" />
                            {{ $t('lead_details.company_name') }}
                          </label>
                          <div class="field-item__value">{{ localLead.company_name }}</div>
                        </div>
                        <div class="field-item field-item--readonly" v-if="localLead.kvk_number">
                          <label>
                            <BaseIcon name="info" size="20" />
                            {{ $t('lead_details.kvk_number') }}
                          </label>
                          <div class="field-item__value">{{ localLead.kvk_number }}</div>
                        </div>
                        <div class="field-item field-item--readonly" v-if="localLead.vat_number">
                          <label>
                            <BaseIcon name="file-text" size="20" />
                            {{ $t('lead_details.vat_number') }}
                          </label>
                          <div class="field-item__value">{{ localLead.vat_number }}</div>
                        </div>
                        <div class="field-item field-item--readonly" v-if="localLead.legal_form">
                          <label>
                            <BaseIcon name="building" size="20" />
                            {{ $t('lead_details.legal_form') }}
                          </label>
                          <div class="field-item__value">{{ legalFormLabel }}</div>
                        </div>
                        <div
                          class="field-item field-item--readonly"
                          v-if="localLead.vat_reverse_charge"
                        >
                          <label>
                            <BaseIcon name="check-circle" size="20" />
                            {{ $t('lead_details.vat_reverse_charge_active') }}
                          </label>
                          <div class="field-item__value">
                            <span class="lead-details__chip">
                              {{ $t('common.yes') || 'Ja' }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="hasBillingDetails"
                        class="lead-details__fields lead-details__fields--readonly"
                      >
                        <h3 class="mb-6">{{ $t('lead_details.billing_details') }}</h3>
                        <div
                          class="field-item field-item--readonly"
                          v-if="localLead.billing_email"
                        >
                          <label>
                            <BaseIcon name="mail" size="20" />
                            {{ $t('lead_details.billing_email') }}
                          </label>
                          <div class="field-item__value">{{ localLead.billing_email }}</div>
                        </div>
                        <div
                          class="field-item field-item--readonly"
                          v-if="localLead.billing_address"
                        >
                          <label>
                            <BaseIcon name="map-pin" size="20" />
                            {{ $t('lead_details.billing_address') }}
                          </label>
                          <div class="field-item__value">
                            {{ formatAddress(localLead.billing_address) }}
                          </div>
                        </div>
                      </div>

                      <AttributionCard
                        class="lead-details__attribution"
                        :attribution="lead?.attribution"
                        :title="$t('lead_details.attribution')"
                      />

                      <!-- Archive/Delete Actions -->
                      <div class="lead-details__actions">
                        <Btn
                          v-if="lead.status !== 'archived'"
                          variant="outlined"
                          size="sm"
                          @click="handleArchive"
                          icon="archive"
                        >
                          {{ $t('lead_details.archive_lead') }}
                        </Btn>
                        <Btn
                          v-else
                          variant="secondary"
                          size="sm"
                          @click="handleRestore"
                          :loading="isRestoring"
                        >
                          <BaseIcon name="refresh" size="16" />
                          {{ $t('lead_details.restore_lead') }}
                        </Btn>
                      </div>
                    </div>
                  </div>

                  <div class="col-lg-6">
                    <div class="lead-details__activities">
                      <h3 class="mb-6">{{ $t('lead_details.activity_feed') }}</h3>
                      <div class="activity-input">
                        <div class="activity-input__field">
                          <TextAreaInput
                            v-model="newNote"
                            :placeholder="$t('lead_details.leave_note_placeholder')"
                            class="activity-input__field"
                          />
                          <Btn variant="secondary" @click="addNote" :disabled="!newNote.trim()">
                            {{ $t('lead_details.add_note') }}
                          </Btn>
                        </div>
                      </div>

                      <div class="activity-list">
                        <div
                          v-for="activity in sortedActivities"
                          :key="activity.id"
                          class="activity-item"
                          :class="{ ['activity-item--' + activity.type]: activity.type }"
                        >
                          <div class="activity-marker">
                            <div class="activity-dot"></div>
                            <div class="activity-line"></div>
                          </div>
                          <CallActivityCard
                            v-if="activity.type === 'call'"
                            :activity="activity"
                            class="activity-call-card"
                          />
                          <div v-else class="activity-content">
                            <div class="activity-timestamp">
                              {{ formatDate(activity.timestamp) }}
                            </div>
                            <div class="activity-text">
                              <template v-if="activity.type === 'submission'">
                                <BaseIcon name="zap" size="14" />
                                {{ $t('lead_details.form_submission') }} - {{ activity.status }}
                              </template>
                              <template v-else-if="activity.type === 'created'">
                                <BaseIcon name="plus-circle" size="14" />
                                {{ $t('lead_details.lead_created') }}
                              </template>
                              <template v-else-if="activity.type === 'note'">
                                <BaseIcon name="notes" size="14" />
                                {{ activity.content }}
                              </template>
                              <template v-else-if="activity.type === 'lead_update'">
                                <BaseIcon name="edit" size="14" />
                                {{ $t('lead_details.updated_lead_fields') }}
                              </template>
                              <template v-else-if="activity.type === 'status_change'">
                                <BaseIcon name="user" size="14" />
                                {{ $t('lead_details.status_updated') }}
                              </template>
                              <template v-else-if="activity.type === 'proposal'">
                                <BaseIcon name="file" size="14" />
                                {{ activity.content }}
                              </template>
                              <template v-else>
                                {{ activity.description }}
                              </template>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tab>

          <Tab
            v-if="isCompany"
            name="account"
            :title="$t('customer_accounts.tab')"
            icon="building"
          >
            <CustomerAccountPanel :lead-id="leadId" :lead-name="headerName" />
          </Tab>

          <Tab name="submissions" :title="$t('lead_details.submissions')">
            <LeadSubmissions :lead-id="leadId" />
          </Tab>

          <Tab name="deals" :title="$t('lead_details.deals')">
            <LeadDeals :lead-id="leadId" />
          </Tab>

          <Tab name="appointments" :title="$t('lead_details.appointments')">
            <LeadAppointments :lead-id="leadId" />
          </Tab>

          <Tab name="proposals" :title="$t('lead_details.proposals')">
            <LeadProposals :lead-id="leadId" />
          </Tab>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, onMounted, ref, computed, watch, getCurrentInstance } from 'vue';
  import { useCrmStore } from '@/store/crmStore';
  import { useToastStore } from '@/store/toastStore';
  import { formatDate } from '@/utils/date';

  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import LeadProposals from '@/components/crm/LeadProposals.vue';
  import LeadSubmissions from '@/components/crm/LeadSubmissions.vue';
  import LeadAppointments from '@/components/crm/LeadAppointments.vue';
  import LeadDeals from '@/components/crm/LeadDeals.vue';
  import CustomerAccountPanel from '@/components/crm/customer/CustomerAccountPanel.vue';
  import CallActivityCard from '@/components/crm/CallActivityCard.vue';
  import AttributionCard from '@/components/crm/AttributionCard.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import InplaceEdit from '@/components/partials/InplaceEdit.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import WysiwygEditor from '@/components/inputs/WysiwygEditor.vue';
  import AddressInput from '@/components/inputs/AddressInput.vue';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useRouter } from 'vue-router';
  import { useLeadAddressSync } from '@/composables/useLeadAddressSync';
  import { formatPhone, toTelHref } from '@/utils/phone';

  export default defineComponent({
    name: 'LeadDetailContent',

    components: {
      LoadingSpinner,
      InplaceEdit,
      TextInput,
      TextAreaInput,
      BaseIcon,
      Btn,
      WysiwygEditor,
      AddressInput,
      Tabs,
      Tab,
      LeadProposals,
      LeadSubmissions,
      LeadAppointments,
      LeadDeals,
      CustomerAccountPanel,
      AvatarElement,
      CallActivityCard,
      AttributionCard,
    },

    emits: ['lead-archived', 'lead-deleted'],

    props: {
      leadId: {
        type: String,
        required: true,
      },
    },

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const loading = ref(true);
      const crmStore = useCrmStore();
      const toastStore = useToastStore();
      const { promptAddressSync } = useLeadAddressSync();
      const activeTab = ref('overview');

      const formatAddress = (address) => {
        if (!address) return '';
        const { street, houseNumber, postalCode, city, country } = address;
        return `${street || ''} ${houseNumber || ''}, ${postalCode || ''} ${city || ''}, ${country || ''}`
          .trim()
          .replace(/\s+/g, ' ');
      };

      const getStatusLabel = (status) => {
        const statusLabels = {
          new: $t('crm.statuses.new'),
          contacted: $t('crm.statuses.contacted'),
          introduction: $t('crm.statuses.introduction'),
          qualified: $t('crm.statuses.qualified'),
          converted: $t('crm.statuses.converted'),
          lost: $t('crm.statuses.lost'),
        };
        return statusLabels[status] || $t('crm.statuses.new');
      };

      const lead = computed(() => crmStore.currentLead);
      const localLead = ref(null);

      const isCompany = computed(() => lead.value?.customer_type === 'company');
      watch(isCompany, (company) => {
        if (!company && activeTab.value === 'account') activeTab.value = 'overview';
      });
      const headerName = computed(() => {
        if (!lead.value) return '';
        if (isCompany.value && lead.value.company_name) return lead.value.company_name;
        return lead.value.name;
      });
      const hasBillingDetails = computed(
        () => Boolean(lead.value?.billing_email || lead.value?.billing_address)
      );
      const legalFormLabel = computed(() => {
        const key = lead.value?.legal_form;
        if (!key) return '';
        const i18nKey = `crm.create_lead.legal_form_options.${key}`;
        const translated = $t(i18nKey);
        return translated && translated !== i18nKey ? translated : key;
      });
      const hasChanges = computed(() => {
        if (!localLead.value || !lead.value) return false;
        return Object.keys(localLead.value).some(
          (key) => JSON.stringify(localLead.value[key]) !== JSON.stringify(lead.value[key])
        );
      });

      const activities = ref([]);
      watch(
        () => lead.value,
        (newLead) => {
          localLead.value = { ...newLead };
          const newActivities = [];

          // Add lead creation as an activity
          if (newLead?.created_at) {
            newActivities.push({
              id: 'lead-created',
              timestamp: new Date(newLead.created_at).getTime(),
              type: 'created',
            });
          }

          // Add submissions as activities
          newLead?.submissions?.forEach((submission) => {
            // Add submission creation activity
            if (submission.created_at) {
              newActivities.push({
                id: `${submission.id}-created`,
                timestamp: new Date(submission.created_at).getTime(),
                type: 'submission',
                status: 'created',
              });
            }
          });

          // Add regular activities (excluding form submissions)
          // Normalize so both API format (created_at, metadata.content) and inline format (timestamp, content) work
          if (newLead?.activities) {
            const formattedActivities = newLead.activities
              .filter((activity) => activity.type !== 'form_submission') // Filter out form submissions
              .map((activity) => {
                const timestamp =
                  activity.timestamp != null
                    ? Number(activity.timestamp)
                    : activity.created_at
                      ? new Date(activity.created_at).getTime()
                      : null;
                if (timestamp == null) return null;
                if (activity.type === 'note') {
                  return {
                    id: activity.id,
                    timestamp,
                    type: 'note',
                    content: activity.metadata?.content ?? activity.content ?? '',
                  };
                }
                if (activity.type === 'lead_update') {
                  return {
                    id: activity.id,
                    timestamp,
                    type: activity.type,
                  };
                }
                if (activity.type === 'call') {
                  // Preserve full payload — CallActivityCard renders metadata + parsed description.
                  return {
                    ...activity,
                    timestamp,
                  };
                }
                // Other types (proposal, etc.): ensure timestamp and content/description for display
                return {
                  ...activity,
                  timestamp,
                  content: activity.content ?? activity.metadata?.content,
                  description: activity.description ?? activity.content ?? activity.metadata?.content,
                };
              })
              .filter(Boolean);
            newActivities.push(...formattedActivities);
          }

          // Add proposal activities
          if (newLead?.proposal_activities) {
            const proposalActivities = newLead.proposal_activities.map((activity) => ({
              id: activity.id,
              timestamp: new Date(activity.created_at).getTime(),
              type: 'proposal',
              proposalId: activity.proposal_id,
              content: 'Proposal "' + activity.proposal_name + '" ' + activity.type,
            }));
            newActivities.push(...proposalActivities);
          }

          // Sort by timestamp descending
          activities.value = newActivities.sort((a, b) => b.timestamp - a.timestamp);
        },
        { immediate: true, deep: true }
      );

      const sortedActivities = computed(() => activities.value);

      const updateField = async (field, value) => {
        const previousValue = localLead.value[field];
        localLead.value[field] = value;
        try {
          // For address field, ensure we send a proper object or null
          const updateData = { [field]: value };
          if (field === 'address') {
            // Check if address has any values
            const hasAddress =
              value &&
              Object.values(value).some(
                (v) => v !== null && v !== undefined && String(v).trim() !== ''
              );
            updateData[field] = hasAddress ? value : null;
          }
          await crmStore.updateLead(props.leadId, updateData);
          toastStore.addToast({
            message: $t('lead_details.updated_lead_field'),
            type: 'success',
            duration: 3000,
          });
          if (field === 'address') {
            await promptAddressSync(props.leadId, { previousAddress: previousValue }, $t);
          }
        } catch (error) {
          localLead.value[field] = lead.value[field];
          if (error.response?.data?.code === 'LEAD_EMAIL_EXISTS') {
            const existing = error.response.data.existingLead;
            toastStore.addToast({
              message: $t('lead_details.email_already_exists', {
                name: existing.name,
                email: existing.email,
              }),
              type: 'error',
              duration: 5000,
            });
          } else {
            toastStore.addToast({
              message: $t('lead_details.update_field_failed'),
              type: 'error',
            });
          }
        }
      };

      const newNote = ref('');

      const addNote = async () => {
        if (!newNote.value.trim()) return;

        const activity = {
          id: Date.now().toString(),
          timestamp: Date.now(),
          type: 'note',
          content: newNote.value.trim(),
        };

        try {
          await crmStore.addLeadActivity(props.leadId, activity);
          newNote.value = '';
          toastStore.addToast({
            message: $t('lead_details.note_added'),
            type: 'success',
            duration: 3000,
          });
        } catch (error) {
          console.error('Failed to add note:', error);
        }
      };

      onMounted(async () => {
        try {
          await crmStore.loadLead(props.leadId);
        } catch (error) {
          console.error('Failed to load lead:', error);
        } finally {
          loading.value = false;
        }
      });

      // Archive/Restore/Delete handlers
      const uiStore = useUIStore();
      const router = useRouter();
      const isRestoring = ref(false);

      const handleArchive = async () => {
        const modalId = uiStore.addModal(null, {
          title: $t('lead_details.archive_lead'),
          description: $t('lead_details.archive_lead_confirm'),
          submitText: $t('lead_details.archive'),
          variant: 'warning',
          onSubmit: async () => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await crmStore.archiveLead(props.leadId);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('lead_details.lead_archived'),
                type: 'success',
              });
              emit('lead-archived', props.leadId);
            } catch (error) {
              console.error('Failed to archive lead:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                message: $t('lead_details.archive_failed'),
                type: 'error',
              });
            }
          },
        });
      };

      const handleRestore = async () => {
        isRestoring.value = true;
        try {
          await crmStore.restoreLead(props.leadId);
          toastStore.addToast({
            message: $t('lead_details.lead_restored'),
            type: 'success',
          });
        } catch (error) {
          console.error('Failed to restore lead:', error);
          toastStore.addToast({
            message: $t('lead_details.restore_failed'),
            type: 'error',
          });
        } finally {
          isRestoring.value = false;
        }
      };

      const openEditModal = async () => {
        const { default: CreateEditLead } = await import('@/components/crm/CreateEditLead.vue');
        const modalId = uiStore.addModal(CreateEditLead, {
          title: $t('crm.create_lead.edit_title'),
          size: 'lg',
          leadId: props.leadId,
          onCreated: () => {
            crmStore.loadLead(props.leadId);
          },
          onClose: () => uiStore.closeModal(modalId),
        });
      };

      const handleDelete = async () => {
        const modalId = uiStore.addModal(null, {
          title: $t('lead_details.delete_lead'),
          description: $t('lead_details.delete_lead_confirm'),
          submitText: $t('lead_details.delete'),
          variant: 'danger',
          onSubmit: async () => {
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await crmStore.deleteLead(props.leadId);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                message: $t('lead_details.lead_deleted'),
                type: 'success',
              });
              emit('lead-deleted', props.leadId);
            } catch (error) {
              console.error('Failed to delete lead:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                message: $t('lead_details.delete_failed'),
                type: 'error',
              });
            }
          },
        });
      };

      return {
        activeTab,
        formatDate,
        formatAddress,
        formatPhone,
        toTelHref,
        getStatusLabel,
        loading,
        lead,
        localLead,
        isCompany,
        headerName,
        hasBillingDetails,
        legalFormLabel,
        sortedActivities,
        updateField,
        hasChanges,
        addNote,
        newNote,
        // Archive/Restore/Delete
        isRestoring,
        handleArchive,
        handleRestore,
        handleDelete,
        openEditModal,
      };
    },
  });
</script>

<style scoped lang="scss">
  .lead-detail-content {
    &__error {
      text-align: center;
      padding: 3rem 1rem;
      color: var(--slate-500);

      .icon {
        color: var(--red-400);
        margin-bottom: 1rem;
      }

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--slate-900);
      }

      p {
        margin: 0;
        font-size: 0.875rem;
      }
    }
  }

  .lead-header {
    @include card;
    margin-bottom: 1rem;

    &__info {
      display: flex;
      gap: 1rem;
    }

    &__avatar {
      flex-shrink: 0;
    }

    &__details {
      flex: 1;
      min-width: 0;
    }

    &__name-row {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      flex-wrap: wrap;
      margin: 0 0 0.25rem 0;
    }

    &__name {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
    }

    &__type-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--indigo-700);
      background: var(--indigo-50);
      border: 1px solid var(--indigo-100);
      padding: 0.1875rem 0.625rem;
      border-radius: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__email {
      font-size: 0.875rem;
      color: var(--slate-600);
      margin: 0 0 0.75rem 0;
    }

    &__meta {
      display: flex;
      align-items: center;
      gap: 1rem;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 4px 8px;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      text-transform: lowercase;

      &--new {
        background-color: var(--blue-50);
        color: var(--blue-700);
        border: 1px solid var(--blue-200);
      }

      &--contacted {
        background-color: var(--amber-50);
        color: var(--amber-700);
        border: 1px solid var(--amber-200);
      }

      &--introduction {
        background-color: var(--indigo-50);
        color: var(--indigo-700);
        border: 1px solid var(--indigo-200);
      }

      &--qualified {
        background-color: var(--teal-50);
        color: var(--teal-700);
        border: 1px solid var(--teal-200);
      }

      &--converted {
        background-color: var(--green-50);
        color: var(--green-700);
        border: 1px solid var(--green-200);
      }

      &--lost {
        background-color: var(--red-50);
        color: var(--red-700);
        border: 1px solid var(--red-200);
      }
    }
  }

  .lead-tabs {
    // @include card;
    // margin-top: 1.5rem;

    :deep(.tabs__nav) {
      border-bottom: 1px solid var(--slate-200);
      padding: 0 1.5rem;
    }

    // :deep(.tabs__content) {
    //   padding: 1.5rem;
    // }
  }

  .lead-details {
    &__main {
      @include card;
    }

    &__actions {
      margin-top: 1.5rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--slate-200);
    }

    &__section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1.5rem;
      // Let the action button wrap below the title instead of overflowing
      // the card edge on narrow viewports.
      flex-wrap: wrap;

      h3 {
        margin: 0;
      }
    }

    &__chip {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 0.5rem;
      background: var(--green-50);
      color: var(--green-700);
      font-size: 0.75rem;
      font-weight: 600;
    }

    &__attribution {
      display: block;
      margin-top: 1.5rem;
    }

    &__fields {
      &--readonly {
        margin-top: 1.5rem;
        padding-top: 1.5rem;
        border-top: 1px solid var(--slate-200);

        h3 {
          margin: 0 0 1rem;
        }
      }

      .field-item {
        padding: 0.5rem 0;
        border-bottom: 1px solid var(--slate-200);

        &:last-child {
          border-bottom: none;
        }

        label {
          font-size: 0.875rem;
          color: var(--slate-600);
          margin-bottom: 0.125rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          // text-transform: uppercase;
          letter-spacing: 0.025em;

          .icon {
            color: var(--slate-400);
          }
        }

        &__value {
          color: var(--slate-900);
          font-size: 0.9375rem;
          line-height: 1.5;
          font-weight: 400;

          &--empty {
            color: var(--slate-400);
            font-style: italic;
          }
        }

        &__phone {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: var(--slate-900);
          font-size: 0.9375rem;
          font-variant-numeric: tabular-nums;
        }

        &__phone-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--brand-50);
          color: var(--brand-600);
          text-decoration: none;
          transition: background 0.15s ease;

          @include hover {
            background: var(--brand-100);
            color: var(--brand-700);
          }
        }

        &__editor {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;

          &--address {
            .address-input {
              margin: 0;
            }
          }

          &-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: flex-end;
            padding-top: 0.5rem;
            border-top: 1px solid var(--slate-200);
          }

          .icon {
            color: var(--slate-500);
            cursor: pointer;
            transition: color 0.2s ease;

            @include hover {
              color: var(--slate-700);
            }
          }

          .wysiwyg-editor {
            flex-grow: 1;
          }
        }

        &__html-content {
          color: var(--slate-900);
          font-size: 0.9375rem;
          line-height: 1.5;
          font-weight: 400;
          padding: 0.5rem 0;

          :deep(b) {
            font-weight: 600;
            color: var(--slate-900);
          }

          :deep(i) {
            font-style: italic;
            color: var(--slate-700);
          }

          :deep(br) {
            display: block;
            content: '';
            margin-top: 0.5rem;
          }
        }

        // InplaceEdit styling
        :deep(.inplace-edit__display) {
          color: var(--slate-900);
          font-size: 0.9375rem;
          line-height: 1.5;
          padding: 0.5rem 0;
          min-height: 1.5rem;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background-color: var(--slate-50);
            border-radius: 0.375rem;
            padding: 0.5rem 0.75rem;
            margin: 0 -0.75rem;
          }
        }

        :deep(.inplace-edit__display--empty) {
          color: var(--slate-400);
          font-style: italic;
        }
      }
    }

    &__activities {
      flex: 1;
      @include card;
      .activity-input {
        margin-bottom: 2rem;
        padding: 1rem;
        background: var(--slate-50);
        border-radius: 0.5rem;

        &__field {
          padding: 0;
          position: relative;
          .btn {
            position: absolute;
            right: 0.5rem;
            bottom: 0.5rem;
          }
          :deep(textarea) {
            width: 100%;
            display: block;
            resize: none;
            height: 50px;
            padding-right: 80px;
          }
        }
      }

      .activity-list {
        position: relative;
        padding-left: 2rem;

        .activity-item {
          position: relative;
          padding: 1rem 0 1rem 0;
          &--note {
            --color: var(--amber-500);
          }
          &--submission {
            --color: var(--brand-500);
          }
          &--created {
            --color: var(--teal-500);
          }
          &--lead_update {
            --color: var(--orange-500);
          }
          &--status_change {
            --color: var(--indigo-500);
          }
          &--proposal {
            --color: var(--amber-500);
          }
          &--call {
            --color: var(--brand-500);
          }

          .activity-call-card {
            margin: 0;
          }

          .activity-marker {
            position: absolute;
            left: -2rem;
            top: 0;
            bottom: 0;
            width: 2px;
            display: flex;
            flex-direction: column;
            align-items: center;

            .activity-dot {
              width: 12px;
              height: 12px;
              background: var(--color);
              border-radius: 50%;
              margin: 1rem 0 0.25rem 0;
            }

            .activity-line {
              flex: 1;
              width: 2px;
              background: var(--slate-200);
            }
          }

          .activity-content {
            background: white;
            border: 1px solid var(--slate-200);
            border-radius: 0.5rem;
            padding: 1rem;

            .activity-timestamp {
              font-size: 0.875rem;
              color: var(--slate-600);
              margin-bottom: 0.25rem;
            }
            .activity-text {
              color: var(--slate-900);
              display: flex;
              align-items: center;
              gap: 0.5rem;

              .icon {
                color: var(--slate-500);
              }
            }
          }
        }
      }
    }
  }

  .status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 0;

    &--new {
      background-color: var(--blue-500);
    }

    &--contacted {
      background-color: var(--amber-500);
    }

    &--introduction {
      background-color: var(--indigo-500);
    }

    &--qualified {
      background-color: var(--teal-500);
    }

    &--converted {
      background-color: var(--green-500);
    }

    &--lost {
      background-color: var(--red-500);
    }
  }
</style>
